import React from 'react';
import styled from '@emotion/styled';
import TextInput from 'components/Wizard/Global/TextInput';
import { getSafeValue } from '../../../utils/security';
import { StyledInfoEllipsis } from '../../Wizard/Global/WizardStyled';
import { StyledAction } from '../StyledComponents';
import Checkbox from '../../Form/Checkbox';
import TableCheckbox from '../../Form/TableCheckbox';
import { StyledContrastPanelColumn } from '../../Wizard/Global/WizardStyledContrastPanel';
import NumericInput from '../../Wizard/Global/NumericInput';
import informationWhiteIcon from '../../../global/img/info-white.svg';
import { DEFAULT_TERRITORY_SKILLS_VALUE, MAP_SETTINGS } from './MapSettingsPanel';

const RowFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`;

const StyledInfoEllipsisFixed = styled(StyledInfoEllipsis)`
  min-width: 120px;
  max-width: 200px;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InputWrapper = styled.div`
  width: 150px;
  min-width: 150px;
  max-width: 150px;
  display: flex;
  align-items: center;

  input,
  div,
  select {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }
`;

const CompactSelect = styled.select`
  appearance: none;
  background: #1f242a;
  color: #fff;
  border: 1px solid #373d45;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 1.2;
  border-radius: 4px;
  height: 40px;
  outline: none;
  &:focus {
    border-color: #4d89f5;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const TRAFFIC_OPTIONS = [
  { key: 'historicalOnly', value: 'historicalOnly' },
  { key: 'liveOrHistorical', value: 'liveOrHistorical' },
  { key: 'automatic', value: 'automatic' },
  { key: 'None', value: 'None' },
];

const MultipleActionSetting = ({
  mapSettingsPanel,
  mapSettings,
  setting,
  handleOnSwitchChange,
  handleOnChangeInput,
  noInfoIcon,
  useTableCheckbox,
}) => {
  const mapSetting = getSafeValue(mapSettings, setting.value);
  const enabled = !!mapSetting?.enabled;
  const translation = getSafeValue(mapSettingsPanel, setting.value);
  const isSpeedFactor = setting.value === MAP_SETTINGS.problem.SPEED_FACTOR;
  const isTraffic = setting.value === MAP_SETTINGS.problem.TRAFFIC;
  const isTerritories = setting.value === MAP_SETTINGS.problem.TERRITORIES;
  const isSkills = setting.value === MAP_SETTINGS.problem.SKILLS;
  const isCategories = setting.value === MAP_SETTINGS.problem.CATEGORIES;
  const useText = isTerritories || isSkills || isCategories;
  const trafficValue =
    (isTraffic &&
      mapSetting &&
      mapSetting.value &&
      TRAFFIC_OPTIONS.find((o) => o.value === mapSetting.value)?.value) ||
    (isTraffic ? 'automatic' : undefined);

  return (
    <StyledContrastPanelColumn
      id={setting.value}
      key={setting.value}
      actionsCount={2.5}
      hasAction
      inputSize="M"
      isEnabled={enabled}
      gap
    >
      {useTableCheckbox ? (
        <RowFlex>
          <TableCheckbox
            total={1}
            selected={enabled ? 1 : 0}
            onSelect={() => handleOnSwitchChange(setting.value, true)}
            onUnselect={() => handleOnSwitchChange(setting.value, false)}
            title=""
            whiteLabel
          />
          <StyledInfoEllipsisFixed alt={translation} title={translation} hasMarginTop>
            {translation}
          </StyledInfoEllipsisFixed>
          <StyledAction>
            <InputWrapper>
              {isTraffic ? (
                <CompactSelect
                  id={`traffic-select-${setting.value}`}
                  value={trafficValue}
                  disabled={!enabled}
                  onChange={(e) => handleOnChangeInput(setting.value, e.target.value)}
                >
                  {TRAFFIC_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.value}>
                      {opt.value}
                    </option>
                  ))}
                </CompactSelect>
              ) : useText ? (
                <TextInput
                  theme="contrast"
                  value={
                    mapSetting?.value !== undefined &&
                    mapSetting?.value !== null &&
                    mapSetting?.value !== ''
                      ? String(mapSetting.value)
                      : DEFAULT_TERRITORY_SKILLS_VALUE
                  }
                  onChange={(value) => handleOnChangeInput(setting.value, value)}
                  disabled={!enabled}
                  title={mapSetting?.value ?? ''}
                  icon={noInfoIcon ? null : informationWhiteIcon}
                />
              ) : (
                <NumericInput
                  theme="contrast"
                  stepValue={isSpeedFactor ? '0.1' : '0.001'}
                  initialValue={
                    mapSetting?.value !== undefined &&
                    mapSetting?.value !== null &&
                    mapSetting?.value !== ''
                      ? String(mapSetting.value)
                      : ''
                  }
                  minValue={isSpeedFactor ? 0.1 : 0}
                  maxValue={isSpeedFactor ? 10 : 100}
                  onChange={(value) => handleOnChangeInput(setting.value, value)}
                  disabled={!enabled}
                  title={mapSetting?.value ?? ''}
                  icon={noInfoIcon ? null : informationWhiteIcon}
                />
              )}
            </InputWrapper>
          </StyledAction>
        </RowFlex>
      ) : (
        <>
          <StyledInfoEllipsis alt={translation} title={translation} hasMarginTop>
            {translation}
          </StyledInfoEllipsis>
          <StyledAction>
            {isTraffic ? (
              <CompactSelect
                id={`traffic-select-${setting.value}`}
                value={trafficValue}
                disabled={!enabled}
                onChange={(e) => handleOnChangeInput(setting.value, e.target.value)}
              >
                {TRAFFIC_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.value}>
                    {opt.value}
                  </option>
                ))}
              </CompactSelect>
            ) : useText ? (
              <TextInput
                theme="contrast"
                value={
                  mapSetting?.value !== undefined &&
                  mapSetting?.value !== null &&
                  mapSetting?.value !== ''
                    ? String(mapSetting.value)
                    : DEFAULT_TERRITORY_SKILLS_VALUE
                }
                onChange={(value) => handleOnChangeInput(setting.value, value)}
                disabled={!enabled}
                title={mapSetting?.value ?? ''}
                icon={noInfoIcon ? null : informationWhiteIcon}
              />
            ) : (
              <NumericInput
                theme="contrast"
                stepValue={isSpeedFactor ? '0.1' : '0.001'}
                initialValue={
                  mapSetting?.value !== undefined &&
                  mapSetting?.value !== null &&
                  mapSetting?.value !== ''
                    ? String(mapSetting.value)
                    : ''
                }
                minValue={isSpeedFactor ? 0.1 : 0}
                maxValue={isSpeedFactor ? 10 : 100}
                onChange={(value) => handleOnChangeInput(setting.value, value)}
                disabled={!enabled}
                title={mapSetting?.value ?? ''}
                icon={noInfoIcon ? null : informationWhiteIcon}
              />
            )}
          </StyledAction>
          <StyledAction>
            <Checkbox
              id={`mapSettingsCheckbox-${setting.value}`}
              value={enabled}
              contrast
              onChange={(value) => handleOnSwitchChange(setting.value, value)}
            />
          </StyledAction>
        </>
      )}
    </StyledContrastPanelColumn>
  );
};
export default MultipleActionSetting;
